﻿<template>
  <div class="mb-cards">
    <!-- Controls -->
    <div class="mb-card mb-cards-controls" v-if="(bookings?.length ?? 0) > 0">
      <label class="mb-search">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
        </svg>
        <input v-model="search" type="search" placeholder="Search bookings" aria-label="Search bookings" />
      </label>
      <div class="mb-sort">
        <label for="sortKey" class="sr-only">Sort by</label>
        <select id="sortKey" v-model="sortKey">
          <option value="departure">Departure date</option>
          <option value="booking">Booking date</option>
          <option value="id">Booking ID</option>
          <option value="reference">Agent ref</option>
          <option value="lead">Lead guest</option>
          <option value="consultant">Consultant</option>
          <option value="status">Status</option>
          <option value="trip">Trip name</option>
        </select>
        <button
          type="button"
          class="mb-sort-dir"
          @click="sortAsc = !sortAsc"
          :aria-pressed="!sortAsc"
          :title="sortAsc ? 'Ascending' : 'Descending'"
          :aria-label="sortAsc ? 'Sorted ascending' : 'Sorted descending'"
        >{{ sortAsc ? '▲' : '▼' }}</button>
      </div>
      <p class="mb-cards-count">
        <template v-if="search.trim()">Showing <strong>{{ Sorted_Bookings.length }}</strong> of <strong>{{ bookings?.length ?? 0 }}</strong> bookings</template>
        <template v-else><strong>{{ bookings?.length ?? 0 }}</strong> {{ (bookings?.length ?? 0) === 1 ? 'booking' : 'bookings' }}</template>
      </p>
    </div>

    <!-- Cards -->
    <article
      v-for="(b, index) in Sorted_Bookings"
      :key="Row_Key(b, index)"
      class="mb-card mb-booking-card"
    >
      <header class="mb-booking-card-head">
        <div class="min-w-0">
          <h3>{{ Trip_Name(b) || '—' }}</h3>
          <div class="mb-booking-card-date" v-if="Departure_Date_Display(b)">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            Departs {{ Departure_Date_Display(b) }}
          </div>
        </div>
        <span v-if="Booking_Status(b)" class="mb-pill" :class="Status_Class(b)">{{ Booking_Status(b) }}</span>
      </header>

      <dl class="mb-booking-card-grid">
        <div>
          <dt>Booking ID</dt>
          <dd class="is-strong">{{ Booking_ID(b) || '—' }}</dd>
        </div>
        <div>
          <dt>Agent ref</dt>
          <dd>{{ Agent_Reference(b) || '—' }}</dd>
        </div>
        <div>
          <dt>Lead guest</dt>
          <dd>{{ Lead_Passenger(b).trim() || '—' }}</dd>
        </div>
        <div>
          <dt>Booked</dt>
          <dd>{{ Booking_Date_Display(b) || '—' }}</dd>
        </div>
        <div>
          <dt>Consultant</dt>
          <dd>{{ Get_Consultant(b) || '—' }}</dd>
        </div>
      </dl>

      <footer class="mb-booking-card-foot">
        <button
          type="button"
          class="mb-view-btn"
          :disabled="loadingIndex !== -1"
          :class="{ 'is-dimmed': loadingIndex !== -1 && loadingIndex !== index }"
          @click="Booking_Details(b, index)"
        >
          <svg v-if="loadingIndex === index" class="mb-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          {{ loadingIndex === index ? 'Loading…' : 'View details' }}
        </button>
      </footer>
    </article>

    <div v-if="(bookings?.length ?? 0) === 0" class="mb-card mb-empty">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
      <strong>No bookings yet</strong>
      <span>Bookings made for this account will appear here.</span>
    </div>
    <div v-else-if="Sorted_Bookings.length === 0" class="mb-card mb-empty">
      <strong>No bookings match "{{ search.trim() }}"</strong>
      <button type="button" class="mb-link-btn" @click="search = ''">Clear search</button>
    </div>

    <details_dialog v-if="detailsOpen" @close="Close_Details" />
  </div>
</template>

<script lang="ts" src="./booking_cards.ts"></script>
